class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends window.StandardEvents.createViewEventElement(HTMLElement) {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', this.debouncedOnChange);
    this.addEventListener('submit', (event) => this.waitForQuantitySave(event));
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || !event.target.matches('.quantity__input')) return;

      event.preventDefault();
      const input = event.target;
      const onChange = () => (this.enterPressedInput = input);
      input.addEventListener('change', onChange, { once: true });
      input.blur();
      input.removeEventListener('change', onChange);
      if (this.enterPressedInput !== input) input.focus();
    });
  }

  cartUpdateUnsubscriber = undefined;

  static pendingCartDataPromise = null;

  static pendingCartRefresh = null;

  static cartRefreshVersion = 0;

  connectedCallback() {
    // The factory base class auto-dispatches cart:view from the
    // `view-event-payload` attribute (Liquid filter output). The drawer
    // sets `view-event-trigger="manual"` to skip auto-dispatch.
    super.connectedCallback();

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // A quantity change already renders its own surface. If both surfaces are
      // present, refresh them together so opening the drawer cannot show old totals.
      if (
        event.source === 'cart-items' &&
        !(document.querySelector('cart-items') && document.querySelector('cart-drawer-items'))
      ) return;
      return this.onCartUpdate();
    });
  }

  // Fetches the full cart shape (used to resolve the cart:lines-update event
  // promise after /cart/add.js, which only returns the added line — not the
  // post-mutation cart aggregates). De-duplicated across concurrent callers.
  static fetchCartData() {
    if (!CartItems.pendingCartDataPromise) {
      const pendingCartDataPromise = fetch(`${routes.cart_url}.json`)
        .then((response) => response.json())
        .catch(() => null)
        .finally(() => {
          if (CartItems.pendingCartDataPromise === pendingCartDataPromise) CartItems.pendingCartDataPromise = null;
        });

      CartItems.pendingCartDataPromise = pendingCartDataPromise;
    }
    return CartItems.pendingCartDataPromise;
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  // Check out waits for a pending quantity save. A quantity edit saves through Ajax after a short wait. A form post
  // at the same time makes iOS Safari cancel the save, and the store answers the post with no page and no message.
  // This flushes the wait, cancels the post, and awaits the save. A saved quantity resumes the post. A rejected one
  // shows its error on the line (updateQuantity), and the buyer stays on the cart. The cart page and the drawer share
  // one wait, also after a re-render replaces the drawer, so a second tap does not post the form twice.
  static pendingQuantitySaves = new Set();
  static waitingForQuantitySave = false;
  // Counts the saves that the store did not accept, also a save that ends while the wait awaits another one.
  static failedQuantitySaves = 0;

  async waitForQuantitySave(event) {
    const { submitter } = event;
    if (!submitter || event.defaultPrevented) return;
    this.debouncedOnChange.flush();
    if (!CartItems.pendingQuantitySaves.size) return;
    event.preventDefault();
    if (CartItems.waitingForQuantitySave) return;
    CartItems.waitingForQuantitySave = true;
    submitter.setAttribute('aria-disabled', 'true');

    const failedSaves = CartItems.failedQuantitySaves;
    try {
      while (CartItems.pendingQuantitySaves.size) {
        await Promise.all(CartItems.pendingQuantitySaves);
        this.debouncedOnChange.flush();
      }
    } finally {
      CartItems.waitingForQuantitySave = false;
      submitter.removeAttribute('aria-disabled');
    }
    if (CartItems.failedQuantitySaves !== failedSaves) return;

    // The save re-rendered the section, so the submitter can be a detached element.
    const button = submitter.isConnected ? submitter : document.getElementById(submitter.id);
    button?.click();
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`) || this.querySelector(`#Drawer-quantity-${id}`);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const name =
      this.enterPressedInput === event.target ? event.target.name : document.activeElement.getAttribute('name');
    this.enterPressedInput = null;
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    // Zero removes a cart line; minimum purchase rules apply only to retained lines.
    if (inputValue !== 0 && inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        name,
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    return CartItems.refreshCart();
  }

  // Keep the page, drawer and header on the same server-rendered snapshot.
  // Updating only innerHTML leaves a restored cart hidden by stale is-empty classes.
  static refreshCart() {
    CartItems.cartRefreshVersion += 1;
    if (CartItems.pendingCartRefresh) return CartItems.pendingCartRefresh;

    CartItems.pendingCartRefresh = Promise.resolve()
      .then(async () => {
        let version;
        do {
          version = CartItems.cartRefreshVersion;
          const parts = [
            { selector: 'cart-items', section: document.getElementById('main-cart-items')?.dataset.id },
            { selector: '#main-cart-footer', section: document.getElementById('main-cart-footer')?.dataset.id },
            { selector: 'cart-drawer', section: 'cart-drawer' },
            { selector: '#cart-icon-bubble', section: 'cart-icon-bubble', sourceSelector: '.shopify-section' },
            { selector: '#cart-live-region-text', section: 'cart-live-region-text', sourceSelector: '.shopify-section' },
          ].filter((part) => part.section && document.querySelector(part.selector));
          if (!parts.length) return;

          const response = await fetch(`${routes.cart_url}?sections=${parts.map((part) => part.section).join(',')}`, {
            credentials: 'same-origin',
            cache: 'no-store',
          });
          if (!response.ok) throw new Error(`Cart refresh returned ${response.status}`);
          const sections = await response.json();
          const snapshots = parts.map((part) => {
            if (typeof sections[part.section] !== 'string') throw new Error('Cart section unavailable');
            const html = new DOMParser().parseFromString(sections[part.section], 'text/html');
            const source = html.querySelector(part.sourceSelector || part.selector);
            if (!source) throw new Error('Cart section incomplete');
            return { ...part, source };
          });

          // An app or a quantity change may have updated the cart during the request.
          if (version !== CartItems.cartRefreshVersion) continue;
          CartItems.applyCartSnapshots(snapshots);
        } while (version !== CartItems.cartRefreshVersion);
      })
      .catch((error) => {
        // A failed refresh must not replace an existing basket with an empty one.
        console.warn('Cart display could not be refreshed.', error);
      })
      .finally(() => {
        CartItems.pendingCartRefresh = null;
      });
    return CartItems.pendingCartRefresh;
  }

  static applyCartSnapshots(snapshots) {
    const focused = document.activeElement;
    const focusId = focused?.id;
    const focusedInDrawer = !!focused?.closest('cart-drawer');

    const syncItems = (target, source) => {
      if (!target || !source) return;
      target.className = source.className;
      target.innerHTML = source.innerHTML;
      const payload = source.getAttribute('view-event-payload');
      if (payload !== null) target.setAttribute('view-event-payload', payload);
      target.lineItemStatusElement = target.querySelector('#shopping-cart-line-item-status, #CartDrawer-LineItemStatus');
    };
    const syncCheckout = (target, source) => {
      target.querySelectorAll('[name="checkout"]').forEach((button) => {
        const replacement = source.querySelector('[name="checkout"]');
        if (replacement) button.disabled = replacement.disabled;
      });
    };
    const syncContents = (target, source, selector) => {
      const current = target.querySelector(selector);
      const replacement = source.querySelector(selector);
      if (current && replacement) current.innerHTML = replacement.innerHTML;
    };

    snapshots.forEach(({ selector, source }) => {
      const target = document.querySelector(selector);
      if (!target) return;
      if (selector === 'cart-items') {
        syncItems(target, source);
      } else if (selector === '#main-cart-footer') {
        target.classList.toggle('is-empty', source.classList.contains('is-empty'));
        syncContents(target, source, '.js-contents');
        syncCheckout(target, source);
      } else if (selector === 'cart-drawer') {
        target.classList.toggle('is-empty', source.classList.contains('is-empty'));
        syncItems(target.querySelector('cart-drawer-items'), source.querySelector('cart-drawer-items'));
        const empty = target.querySelector('.drawer__inner-empty');
        const replacementEmpty = source.querySelector('.drawer__inner-empty');
        if (replacementEmpty && !empty) target.querySelector('.drawer__inner').prepend(replacementEmpty);
        if (empty && !replacementEmpty) empty.remove();
        syncContents(target, source, '.cart-drawer__footer');
        syncCheckout(target, source);
      } else {
        target.innerHTML = source.innerHTML;
      }
    });

    if (focused && !focused.isConnected) {
      const replacement = focusId && document.getElementById(focusId);
      const drawer = document.querySelector('cart-drawer.active');
      if (focusedInDrawer && drawer) {
        const inner = drawer.querySelector('.drawer__inner');
        trapFocus(inner, replacement || inner);
      } else {
        replacement?.focus({ preventScroll: true });
      }
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    CartItems.cartRefreshVersion += 1;
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    const action = quantity === 0 ? 'remove' : 'update';
    const quantityInput = this.querySelector(`#Quantity-${line}`) || this.querySelector(`#Drawer-quantity-${line}`);
    const lineVariantId = variantId || quantityInput?.dataset.quantityVariantId;
    const lineKey = quantityInput?.dataset.quantityLineKey;
    const linesUpdateDeferred = this.createCartLinesUpdateEvent(action, lineVariantId, quantity, lineKey);
    let saved = false;

    // Cache sections before the fetch so we read dataset.id while elements still exist in the DOM
    const sectionsToRender = this.getSectionsToRender();

    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionsToRender.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    const save = fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        saved = response.ok;
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          saved = false;
          this.dispatchCartErrorEvent(parsedState.errors, 'INVALID');
          linesUpdateDeferred?.reject(new Error(parsedState.errors));
        } else {
          this.resolveCartLinesUpdate(linesUpdateDeferred, parsedState);
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            this.querySelector(`#CartItem-${line}, #CartDrawer-Item-${line}`)
              ?.querySelector(`[name="${name}"]`)
              ?.focus();
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          sectionsToRender.forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper?.querySelector('.drawer__inner-empty')) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((e) => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        this.dispatchCartErrorEvent(window.cartStrings.error, 'SERVICE_UNAVAILABLE');
        linesUpdateDeferred?.reject(e);
      })
      .finally(() => {
        CartItems.pendingQuantitySaves.delete(save);
        // Only the store's answer decides the save. An error in the theme code around it does not.
        if (!saved) CartItems.failedQuantitySaves++;
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      })
      .catch((e) => {
        // An error in the theme code above must not stop the Check out wait.
        console.error(e);
      });
    CartItems.pendingQuantitySaves.add(save);
  }

  createCartLinesUpdateEvent(action, variantId, quantity, lineKey) {
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent || !variantId) return null;
    // No AJAX line key on the row — likely cached HTML rendered before this
    // attribute landed. Skip dispatch rather than emit an event with id: ''.
    if (!lineKey) return null;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: [{ id: lineKey, quantity }],
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  resolveCartLinesUpdate(deferred, parsedState) {
    if (!deferred) return;
    const { CartLinesUpdateEvent } = window.StandardEvents || {};
    if (!CartLinesUpdateEvent) return;

    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState) });
  }

  dispatchCartErrorEvent(message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (!CartErrorEvent) return;
    this.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

window.addEventListener('pageshow', (event) => {
  const navigation = performance.getEntriesByType('navigation')[0];
  let returnedFromCheckout = false;
  try {
    returnedFromCheckout = /\/checkouts?(?:\/|$)/.test(new URL(document.referrer).pathname);
  } catch {}
  if (event.persisted || navigation?.type === 'back_forward' || returnedFromCheckout) {
    void CartItems.refreshCart();
  }
});

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const newNote = event.target.value;
            const noteDeferred = this.dispatchNoteUpdateEvent(newNote);

            const body = JSON.stringify({ note: newNote });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then((r) => r.json())
              .then((cart) => {
                if (!cart || cart.errors) {
                  throw Object.assign(new Error(cart?.errors), { code: 'INVALID' });
                }

                if (noteDeferred) {
                  const { CartNoteUpdateEvent } = window.StandardEvents || {};
                  if (CartNoteUpdateEvent) {
                    noteDeferred.resolve({ cart: CartNoteUpdateEvent.createCartFromAjaxResponse(cart) });
                  }
                }
                CartPerformance.measureFromEvent('note-update:user-action', event);
              })
              .catch((e) => {
                noteDeferred?.reject(e);
                const { CartErrorEvent } = window.StandardEvents || {};
                if (CartErrorEvent) {
                  this.dispatchEvent(
                    new CartErrorEvent({
                      error: e.message || 'Note update failed',
                      code: e.code || 'SERVICE_UNAVAILABLE',
                    })
                  );
                }
              });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }

      dispatchNoteUpdateEvent(newNote) {
        const { CartNoteUpdateEvent } = window.StandardEvents || {};
        if (!CartNoteUpdateEvent) return null;

        const context = this.closest('dialog') || this.closest('cart-drawer') ? 'dialog' : 'cart';
        const deferred = CartNoteUpdateEvent.createPromise();

        this.dispatchEvent(
          new CartNoteUpdateEvent({
            context,
            note: newNote,
            promise: deferred.promise,
          })
        );

        return deferred;
      }
    }
  );
}
